import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axiosInstance from '../api/axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import { User, Lock, MapPin, Trash2, Plus, Save } from 'lucide-react';

const Profile = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    label: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'Germany',
  });
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [reminderConsent, setReminderConsent] = useState(false);
  const [reminderConsentAt, setReminderConsentAt] = useState(null);
  const [consentBusy, setConsentBusy] = useState(false);

  const fetchReminderConsent = async () => {
    try {
      const r = await axiosInstance.get('/cart/reminder-consent');
      setReminderConsent(Boolean(r.data?.enabled));
      setReminderConsentAt(r.data?.consent_at || null);
    } catch (e) { /* silently ignore — feature optional */ }
  };

  const toggleReminderConsent = async (enabled) => {
    setConsentBusy(true);
    try {
      await axiosInstance.put('/cart/reminder-consent', { enabled });
      setReminderConsent(enabled);
      if (enabled) {
        setReminderConsentAt(new Date().toISOString());
        toast.success('Vielen Dank! Wir erinnern dich an verlassene Warenkörbe.');
      } else {
        setReminderConsentAt(null);
        toast.success('Einwilligung widerrufen — keine Warenkorb-Emails mehr.');
      }
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setConsentBusy(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProfileData();
    fetchReminderConsent();
  }, [isAuthenticated]);

  const fetchProfileData = async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      setProfileData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
      });
      setAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Fehler beim Laden des Profils');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.put('/users/profile', profileData);
      toast.success('Profil erfolgreich aktualisiert!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Neue Passwörter stimmen nicht überein');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.put('/users/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Passwort erfolgreich geändert!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post('/users/addresses', newAddress);
      toast.success('Adresse hinzugefügt!');
      setShowAddAddress(false);
      setNewAddress({
        label: '',
        street: '',
        house_number: '',
        postal_code: '',
        city: '',
        country: 'Germany',
      });
      fetchProfileData();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen der Adresse');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!confirm('Möchten Sie diese Adresse wirklich löschen?')) return;

    try {
      await axiosInstance.delete(`/users/addresses/${addressId}`);
      toast.success('Adresse gelöscht');
      fetchProfileData();
    } catch (error) {
      toast.error('Fehler beim Löschen der Adresse');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Mein Profil</h1>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="profile">
                <User className="h-4 w-4 mr-2" />
                Profil
              </TabsTrigger>
              <TabsTrigger value="password">
                <Lock className="h-4 w-4 mr-2" />
                Passwort
              </TabsTrigger>
              <TabsTrigger value="addresses">
                <MapPin className="h-4 w-4 mr-2" />
                Adressen
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle>Persönliche Daten</CardTitle>
                  <CardDescription>
                    Aktualisieren Sie Ihre persönlichen Informationen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first_name">Vorname *</Label>
                        <Input
                          id="first_name"
                          value={profileData.first_name}
                          onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name">Nachname *</Label>
                        <Input
                          id="last_name"
                          value={profileData.last_name}
                          onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">E-Mail-Adresse *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Telefonnummer</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+49 123 456789"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      style={{ backgroundColor: '#C4A084', color: 'white' }}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Wird gespeichert...' : 'Änderungen speichern'}
                    </Button>
                  </form>

                  {/* DSGVO: Cart Reminder Consent */}
                  <div className="mt-8 pt-6 border-t border-gray-200" data-testid="reminder-consent-section">
                    <h3 className="font-semibold text-[#5D4E3C] mb-2 flex items-center gap-2">
                      📧 Email-Benachrichtigungen
                    </h3>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reminderConsent}
                        onChange={(e) => toggleReminderConsent(e.target.checked)}
                        disabled={consentBusy}
                        className="mt-1 h-4 w-4 accent-[#C4A084]"
                        data-testid="reminder-consent-checkbox"
                      />
                      <div className="flex-1 text-sm text-gray-700 leading-relaxed">
                        <strong className="text-[#5D4E3C]">Warenkorb-Erinnerungen erhalten</strong>
                        <p className="text-xs text-gray-500 mt-1">
                          Wenn du Artikel im Warenkorb vergisst, schicken wir dir nach 24 h eine
                          freundliche Erinnerungs-Email — eine Email pro Warenkorb, kein Spam, keine Werbung.
                          Du kannst diese Einwilligung jederzeit hier oder per Link in der Email widerrufen.
                        </p>
                        {reminderConsentAt && (
                          <p className="text-[10px] text-gray-400 mt-1">
                            Zugestimmt am {new Date(reminderConsentAt).toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Password Tab */}
            <TabsContent value="password">
              <Card>
                <CardHeader>
                  <CardTitle>Passwort ändern</CardTitle>
                  <CardDescription>
                    Aktualisieren Sie Ihr Passwort für mehr Sicherheit
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <Label htmlFor="current_password">Aktuelles Passwort *</Label>
                      <Input
                        id="current_password"
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="new_password">Neues Passwort *</Label>
                      <Input
                        id="new_password"
                        type="password"
                        placeholder="Mindestens 6 Zeichen"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="confirm_password">Neues Passwort bestätigen *</Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      style={{ backgroundColor: '#C4A084', color: 'white' }}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      {loading ? 'Wird geändert...' : 'Passwort ändern'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Addresses Tab */}
            <TabsContent value="addresses">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Gespeicherte Adressen</CardTitle>
                      <CardDescription>
                        Verwalten Sie Ihre Lieferadressen
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => setShowAddAddress(!showAddAddress)}
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Neue Adresse
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {showAddAddress && (
                    <Card className="border-2 border-dashed">
                      <CardContent className="pt-6">
                        <form onSubmit={handleAddAddress} className="space-y-4">
                          <div>
                            <Label htmlFor="label">Bezeichnung *</Label>
                            <Input
                              id="label"
                              placeholder="z.B. Zuhause, Arbeit"
                              value={newAddress.label}
                              onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                              required
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                              <Label htmlFor="street">Straße *</Label>
                              <Input
                                id="street"
                                value={newAddress.street}
                                onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="house_number">Hausnr. *</Label>
                              <Input
                                id="house_number"
                                value={newAddress.house_number}
                                onChange={(e) => setNewAddress({ ...newAddress, house_number: e.target.value })}
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor="postal_code">PLZ *</Label>
                              <Input
                                id="postal_code"
                                value={newAddress.postal_code}
                                onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <Label htmlFor="city">Stadt *</Label>
                              <Input
                                id="city"
                                value={newAddress.city}
                                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                required
                              />
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              type="submit"
                              disabled={loading}
                              className="rounded-none text-white"
                              
                            >
                              Adresse speichern
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowAddAddress(false)}
                              className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                            >
                              Abbrechen
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  )}

                  {addresses.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      Keine gespeicherten Adressen
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addresses.map((address, index) => (
                        <Card key={index}>
                          <CardContent className="pt-6">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-semibold">{address.label}</h3>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAddress(address.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                            <p className="text-sm text-gray-600">
                              {address.street} {address.house_number}<br />
                              {address.postal_code} {address.city}<br />
                              {address.country}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Profile;
